import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Typography, Grid, Box,
    IconButton, CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { Unidade, Obra } from '../services/types';
import { getObras } from '../services/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabaseClient';

interface UnidadeFormModalProps {
    open: boolean;
    onClose: () => void;
    unidade?: Unidade | null;
}



const UnidadeFormModal: React.FC<UnidadeFormModalProps> = ({ open, onClose, unidade }) => {
    const queryClient = useQueryClient();
    const [uploading, setUploading] = useState(false);
    const { data: obras = [] } = useQuery<Obra[]>({
        queryKey: ['obras-select-unidades'],
        queryFn: getObras,
        enabled: open,
    });
    const [formData, setFormData] = useState<Partial<Unidade>>({
        obra_id: null,
        identificacao: '',
        descricao: '',
        endereco: '',
        bairro: '',
        municipio: '',
        estado: '',
        cep: '',
        valor: '' as any,
        status: 'Disponível',
        area: '' as any,
        area_construida: '' as any,
        area_nao_construida: '' as any,
        area_privativa_total: '' as any,
        quartos: '' as any,
        banheiros: '' as any,
        vagas: '' as any,
        imagens: []
    });

    useEffect(() => {
        if (unidade) {
            const areaPriv =
                unidade.area_privativa_total ??
                unidade.area ??
                (unidade.area_construida != null || unidade.area_nao_construida != null
                    ? Number(((Number(unidade.area_construida) || 0) + (Number(unidade.area_nao_construida) || 0)).toFixed(2))
                    : ('' as any));
            setFormData({
                ...unidade,
                obra_id: unidade.obra_id ?? null,
                valor: unidade.valor === 0 ? ('' as any) : unidade.valor,
                area: areaPriv === 0 ? ('' as any) : areaPriv,
                area_construida: unidade.area_construida === 0 || unidade.area_construida == null ? ('' as any) : unidade.area_construida,
                area_nao_construida: unidade.area_nao_construida === 0 || unidade.area_nao_construida == null ? ('' as any) : unidade.area_nao_construida,
                area_privativa_total: areaPriv === 0 ? ('' as any) : areaPriv,
                quartos: unidade.quartos === 0 ? ('' as any) : unidade.quartos,
                banheiros: unidade.banheiros === 0 ? ('' as any) : unidade.banheiros,
                vagas: unidade.vagas === 0 ? ('' as any) : unidade.vagas,
            });
        } else {
            setFormData({
                obra_id: null,
                identificacao: '',
                descricao: '',
                endereco: '',
                bairro: '',
                municipio: '',
                estado: '',
                cep: '',
                valor: '' as any,
                status: 'Disponível',
                area: '' as any,
                area_construida: '' as any,
                area_nao_construida: '' as any,
                area_privativa_total: '' as any,
                quartos: '' as any,
                banheiros: '' as any,
                vagas: '' as any,
                imagens: []
            });
        }
    }, [unidade, open]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'obra_id') {
            const obraId = value === '' ? null : Number(value);
            const selectedObra = obras.find(o => o.id === obraId);
            setFormData(prev => ({
                ...prev,
                obra_id: obraId,
                endereco: prev.endereco || selectedObra?.endereco || '',
                municipio: prev.municipio || selectedObra?.municipio || '',
                estado: prev.estado || selectedObra?.estado || '',
            }));
            return;
        }
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            if (name === 'area_construida' || name === 'area_nao_construida') {
                const constr = next.area_construida !== ('' as any) && next.area_construida != null ? Number(next.area_construida) : null;
                const naoConstr = next.area_nao_construida !== ('' as any) && next.area_nao_construida != null ? Number(next.area_nao_construida) : null;
                if (constr != null || naoConstr != null) {
                    const priv = Number(((constr || 0) + (naoConstr || 0)).toFixed(2));
                    next.area_privativa_total = priv;
                    next.area = priv;
                } else {
                    next.area_privativa_total = '' as any;
                    next.area = '' as any;
                }
            } else if (name === 'area_privativa_total') {
                next.area = value as any;
            }
            return next;
        });
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return;
        setUploading(true);

        const files = Array.from(event.target.files);
        const newImages: string[] = [];

        try {
            for (const file of files) {
                const fileExt = file.name.split('.').pop();
                const filePath = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;

                const { error: uploadError } = await supabase.storage
                    .from('unidades')
                    .upload(filePath, file);

                if (uploadError) throw uploadError;

                const { data } = supabase.storage.from('unidades').getPublicUrl(filePath);
                newImages.push(data.publicUrl);
            }

            setFormData(prev => ({
                ...prev,
                imagens: [...(prev.imagens || []), ...newImages]
            }));
        } catch (error: any) {
            alert('Erro ao fazer upload da imagem: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveImage = (index: number) => {
        setFormData(prev => ({
            ...prev,
            imagens: prev.imagens?.filter((_, i) => i !== index)
        }));
    };

    const mutation = useMutation({
        mutationFn: async (data: Partial<Unidade>) => {
            const { error, data: result } = await supabase.rpc('upsert_unidade', {
                p_id: unidade?.id || null,
                p_identificacao: data.identificacao,
                p_descricao: data.descricao,
                p_endereco: data.endereco,
                p_bairro: data.bairro,
                p_municipio: data.municipio,
                p_estado: data.estado,
                p_cep: data.cep,
                p_valor: Number(data.valor),
                p_status: data.status,
                p_area: data.area_privativa_total ? Number(data.area_privativa_total) : data.area ? Number(data.area) : null,
                p_quartos: data.quartos ? Number(data.quartos) : null,
                p_banheiros: data.banheiros ? Number(data.banheiros) : null,
                p_vagas: data.vagas ? Number(data.vagas) : null,
                p_imagens: data.imagens || [],
                p_obra_id: data.obra_id ? Number(data.obra_id) : null,
                p_area_construida: data.area_construida ? Number(data.area_construida) : null,
                p_area_nao_construida: data.area_nao_construida ? Number(data.area_nao_construida) : null,
                p_area_privativa_total: data.area_privativa_total ? Number(data.area_privativa_total) : data.area ? Number(data.area) : null,
            });
            if (error) throw error;
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['unidades'] });
            queryClient.invalidateQueries({ queryKey: ['obras-resumo'] });
            onClose();
        },
        onError: (error: any) => {
            alert('Erro ao salvar unidade: ' + error.message);
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        mutation.mutate(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{unidade ? 'Editar Unidade' : 'Nova Unidade'}</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                name="obra_id"
                                select
                                label="Obra Vinculada (Opcional)"
                                fullWidth
                                value={formData.obra_id ?? ''}
                                onChange={handleChange}
                            >
                                <MenuItem value="">
                                    <em>Nenhuma obra vinculada (Unidade Avulsa)</em>
                                </MenuItem>
                                {obras.map((obra) => (
                                    <MenuItem key={obra.id} value={obra.id}>
                                        {obra.nome} {obra.municipio ? `(${obra.municipio}/${obra.estado})` : ''}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                name="identificacao"
                                label="Identificação (Ex: Apto 101)"
                                fullWidth
                                required
                                value={formData.identificacao}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                name="status"
                                select
                                label="Status"
                                fullWidth
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <MenuItem value="Disponível">Disponível</MenuItem>
                                <MenuItem value="Reservada">Reservada</MenuItem>
                                <MenuItem value="Vendida">Vendida</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                name="descricao"
                                label="Descrição Detalhada"
                                fullWidth
                                multiline
                                rows={3}
                                value={formData.descricao}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                name="valor"
                                label="Valor de Venda (R$)"
                                type="number"
                                fullWidth
                                required
                                value={formData.valor || ''}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                name="vagas"
                                label="Vagas de Garagem"
                                type="number"
                                fullWidth
                                value={formData.vagas || ''}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                name="area_construida"
                                label="Área Construída (m²)"
                                type="number"
                                fullWidth
                                value={formData.area_construida || ''}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                name="area_nao_construida"
                                label="Área Não Construída (m²)"
                                type="number"
                                fullWidth
                                value={formData.area_nao_construida || ''}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                name="area_privativa_total"
                                label="Área Privativa Total (m²)"
                                type="number"
                                fullWidth
                                value={formData.area_privativa_total || ''}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* Endereço */}
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Localização</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                name="cep"
                                label="CEP"
                                fullWidth
                                value={formData.cep}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 8 }}>
                            <TextField
                                name="endereco"
                                label="Logradouro"
                                fullWidth
                                value={formData.endereco}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                name="bairro"
                                label="Bairro"
                                fullWidth
                                value={formData.bairro}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                name="municipio"
                                label="Município"
                                fullWidth
                                value={formData.municipio}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                name="estado"
                                label="Estado"
                                fullWidth
                                value={formData.estado}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* Características */}
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Características</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                name="quartos"
                                label="Quartos"
                                type="number"
                                fullWidth
                                value={formData.quartos || ''}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                name="banheiros"
                                label="Banheiros"
                                type="number"
                                fullWidth
                                value={formData.banheiros || ''}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* Imagens */}
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Fotos da Unidade</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                {formData.imagens?.map((url, index) => (
                                    <Box key={index} sx={{ position: 'relative', width: 100, height: 100, borderRadius: 1, overflow: 'hidden' }}>
                                        <img src={url} alt={`Foto ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveImage(index)}
                                            sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ width: 100, height: 100, display: 'flex', flexDirection: 'column', gap: 1, borderStyle: 'dashed' }}
                                    disabled={uploading}
                                >
                                    {uploading ? <CircularProgress size={24} /> : <CloudUploadIcon />}
                                    <span style={{ fontSize: '0.7rem' }}>Adicionar</span>
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageUpload}
                                    />
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button type="submit" variant="contained" disabled={mutation.isPending || uploading}>
                        {mutation.isPending ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default UnidadeFormModal;
